'use client';
import classNames from 'classnames';
import React from 'react';
import { MaterialIconName } from '../types/materialIconName';
import { MaterialIcon } from './MaterialIcon';

interface SectionButtonProps {
  isOpen?: boolean;
  toggleVisibility?: () => void;
  iconName?: MaterialIconName;
  className?: string;
  iconClassName?: string;
  children?: React.ReactNode;
  showExpandIcon?: boolean;
  verticalAlign?: boolean;
  endIconName?: MaterialIconName;
}

const SectionButton = ({
  isOpen,
  toggleVisibility,
  iconName,
  className,
  iconClassName,
  children,
  showExpandIcon = true,
  verticalAlign = false,
  endIconName,
}: SectionButtonProps) => {
  return (
    <div
      role="button"
      className={classNames(
        'box-border rounded-radius/button border p-spacing/gap/soft-12px md:h-10 lg:h-12',
        'transition-all duration-300 ease-linear',
        {
          'bg-fill/base text-gray-900': !isOpen,
          'mhg-alpha-bg-fill-primary-light mhg-alpha-text-fill-primary-dark cursor-pointer':
            isOpen,
          'hover:bg-fill/alternative-1 hover:border-borders/default': !isOpen,
          'border-transparent': !isOpen,
        },
        className,
      )}
      onClick={toggleVisibility}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggleVisibility?.();
        }
      }}
      tabIndex={0}
    >
      <div
        className={classNames('flex w-full items-center gap-spacing/gap/y', {
          'justify-between': showExpandIcon && !verticalAlign,
          'justify-center': !showExpandIcon && !verticalAlign,
          'justify-start': verticalAlign,
        })}
      >
        <div className="flex items-center justify-center gap-2 md:gap-0">
          {!!iconName && (
            <div className="flex items-center">
              <MaterialIcon
                icon={iconName}
                className={classNames(
                  iconClassName,
                  'md:mr-spacing/gap/soft-6px md:hidden',
                )}
                px={18}
              />
              <MaterialIcon
                icon={iconName}
                className={classNames(
                  iconClassName,
                  'md:mr-spacing/gap/soft-6px hidden md:block',
                )}
                px={22}
              />
            </div>
          )}
          <span className="mhg-alpha-bullet-1">
            <span className="flex items-center gap-spacing/gap/soft-6px">
              {children}
            </span>
          </span>
        </div>

        {showExpandIcon && (
          <MaterialIcon
            icon={endIconName ? endIconName : 'expand_less'}
            className={classNames('overflow-visible transition-transform', {
              'rotate-0': isOpen && !endIconName,
              'rotate-180': !isOpen && !endIconName,
            })}
          />
        )}
      </div>
    </div>
  );
};

export default SectionButton;
